import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Clock, ArrowLeft, Save, AlertCircle, FileText, Trash2, Settings, RefreshCw, Phone, Mail, User, Download, X, Loader2, Check, Square, Music, CheckCircle, ChevronRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { createEvent, getEventsByDjId, Event as DJEvent } from '../../services/events';
import { getRequestsByEventId, importSongsFromPastEvent, SongRequest, createRequest } from '../../services/requests';
import type { LibrarySong } from '../../services/songMatchMemory';
import LibraryQueueModal from '../../components/dj/LibraryQueueModal';
import { getTemplatesByDjId, deleteTemplate, EventTemplate } from '../../services/templates';
import { getPollTemplates, type PollTemplate } from '../../services/polls';
import { addLibraryPollsToEvent, selectPollsForNewEvent } from '../../services/pollLibrary';
import { getDJDefaults, saveDJDefaults, DJDefaultEventSettings, SYSTEM_DEFAULTS, playerQueueSettingsFromDefaults } from '../../services/djDefaults';
import { LEGACY_PLAY_MODE, playModeConfig, type PlayMode } from '../../services/playModes';
import { PlayModePicker } from '../../components/dj/PlayModePicker';
import { partitionForImport } from '../../utils/importPartition';
import { libraryRequestData } from '../../utils/libraryRequest';
import { duplicateEventNameMessage, findDuplicateEventName } from '../../utils/eventNames';
import { useFormDraft } from '../../hooks/useFormDraft';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import toast from 'react-hot-toast';

// Get today's date in YYYY-MM-DD format
function getTodayDate(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Get default start time (next hour, rounded)
function getDefaultStartTime(): string {
  const now = new Date();
  now.setHours(now.getHours() + 1);
  now.setMinutes(0);
  return now.toTimeString().slice(0, 5); // HH:MM format
}

// Storage key for form draft
const CREATE_EVENT_DRAFT_KEY = 'votebeats_create_event_draft';

// Default form values
const defaultFormValues = {
  name: '',
  date: getTodayDate(),
  time: getDefaultStartTime(),
  endTime: '',
  location: '',
  address: '',
  description: '',
  contactName: '',
  contactPhone: '',
  contactEmail: '',
};

export default function CreateEventPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Use form draft hook for persistence
  const {
    values: formValues,
    setValue,
    clearDraft,
    hasLoadedDraft,
    isDirty,
  } = useFormDraft({
    storageKey: CREATE_EVENT_DRAFT_KEY,
    defaultValues: defaultFormValues,
    debounceMs: 300,
  });

  // Destructure form values for easier access
  const { name, date, time, endTime, location, address, description, contactName, contactPhone, contactEmail } = formValues;
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Play mode (ITER-121) — no system default, so a first-time DJ must pick
  const [playMode, setPlayMode] = useState<PlayMode | null>(null);
  const [hasSavedDefaultMode, setHasSavedDefaultMode] = useState(false);
  const [makeModeDefault, setMakeModeDefault] = useState(true);
  const selectedModeConfig = playMode ? playModeConfig(playMode) : null;

  // Templates state
  const [templates, setTemplates] = useState<EventTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');

  // DJ default settings state
  const [djDefaults, setDjDefaults] = useState<DJDefaultEventSettings>(SYSTEM_DEFAULTS);

  // Currently active settings (either from defaults or loaded template)
  const [activeSettings, setActiveSettings] = useState<DJDefaultEventSettings>(SYSTEM_DEFAULTS);
  const [settingsSource, setSettingsSource] = useState<'defaults' | 'template'>('defaults');

  // Import from Past Event state
  const [showImportModal, setShowImportModal] = useState(false);
  // Library Queue modal state
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [stagedLibrarySongs, setStagedLibrarySongs] = useState<LibrarySong[]>([]);
  const [importStep, setImportStep] = useState<'pick-event' | 'pick-songs'>('pick-event');
  const [pastEvents, setPastEvents] = useState<DJEvent[]>([]);
  const [pastEventsLoading, setPastEventsLoading] = useState(false);
  const [selectedPastEvent, setSelectedPastEvent] = useState<DJEvent | null>(null);
  const [pastEventSongs, setPastEventSongs] = useState<SongRequest[]>([]);
  const [pastSongsLoading, setPastSongsLoading] = useState(false);
  const [selectedSongIds, setSelectedSongIds] = useState<Set<string>>(new Set());
  // My Polls library (ITER-146): polls that come along into the new event
  const [pollLibrary, setPollLibrary] = useState<PollTemplate[]>([]);
  const [pollLibraryError, setPollLibraryError] = useState(false);
  const pollsComingAlong = useMemo(
    () => selectPollsForNewEvent(pollLibrary, selectedTemplate || null),
    [pollLibrary, selectedTemplate]
  );
  // Songs staged for import (confirmed in modal, imported after event creation)
  const [stagedImportSongs, setStagedImportSongs] = useState<SongRequest[]>([]);
  const [stagedSourceEventName, setStagedSourceEventName] = useState<string>('');

  // Load templates, DJ defaults, and profile contact info on mount
  useEffect(() => {
    if (user) {
      const loadData = async () => {
        const djTemplates = await getTemplatesByDjId(user.uid);
        setTemplates(djTemplates);

        // Load DJ's custom default settings
        const defaults = await getDJDefaults(user.uid);
        setDjDefaults(defaults);
        setActiveSettings(defaults);
        setPlayMode(defaults.playMode ?? null);
        setHasSavedDefaultMode(Boolean(defaults.playMode));
        setMakeModeDefault(!defaults.playMode);

        // Pre-fill contact info from DJ profile (only if form is fresh / no draft loaded)
        if (!isDirty) {
          try {
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            if (userDoc.exists()) {
              const data = userDoc.data();
              if (data.defaultContactName || data.defaultContactPhone || data.defaultContactEmail) {
                setValue('contactName', data.defaultContactName || user.displayName || '');
                setValue('contactPhone', data.defaultContactPhone || '');
                setValue('contactEmail', data.defaultContactEmail || user.email || '');
              } else {
                // Fall back to Firebase profile info
                if (user.displayName) setValue('contactName', user.displayName);
                if (user.email) setValue('contactEmail', user.email);
              }
            }
          } catch {
            // Silent fail — contact info is optional
          }
        }
      };
      loadData();

      getPollTemplates(user.uid)
        .then(library => {
          setPollLibrary(library);
          setPollLibraryError(false);
        })
        .catch(error => {
          console.error('[CreateEventPage] Could not load the poll library:', error);
          setPollLibraryError(true);
        });
    }
  }, [user]);

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplate(templateId);

    if (!templateId) {
      // Reset to DJ defaults when deselecting template
      setActiveSettings(djDefaults);
      setSettingsSource('defaults');
      toast.success('Reverted to your default settings');
      return;
    }

    // Find and load the selected template
    const template = templates.find(t => t.id === templateId);
    if (template) {
      // Map template settings to active settings format, filling missing fields from DJ defaults
      setActiveSettings({
        ...djDefaults,
        isActive: template.settings.requestsOpen,
        autoApprove: template.settings.autoApprove,
        filterExplicit: template.settings.blockExplicit,
        blockExplicit: template.settings.blockExplicit,
        warnExplicit: template.settings.warnExplicit ?? djDefaults.warnExplicit,
        cooldownMinutes: template.settings.cooldownMinutes ?? djDefaults.cooldownMinutes,
        maxRequestsPerUser: template.settings.maxRequestsPerUser ?? djDefaults.maxRequestsPerUser,
      });
      setSettingsSource('template');
      toast.success(`Loaded template "${template.name}"`);
    }
  };

  const handleDeleteTemplate = async (templateId: string, templateName: string, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!confirm(`Are you sure you want to delete the template "${templateName}"?`)) {
      return;
    }

    try {
      await deleteTemplate(templateId);
      setTemplates(templates.filter(t => t.id !== templateId));
      if (selectedTemplate === templateId) {
        setSelectedTemplate('');
      }
      toast.success('Template deleted');
    } catch {
      toast.error('Failed to delete template');
    }
  };

  // ---- Import from Past Event handlers ----
  const handleOpenImportModal = async () => {
    if (!user?.uid) return;
    setShowImportModal(true);
    setImportStep('pick-event');
    setSelectedPastEvent(null);
    setPastEventSongs([]);
    setSelectedSongIds(new Set());
    setPastEventsLoading(true);
    try {
      const events = await getEventsByDjId(user.uid);
      setPastEvents(events);
    } catch {
      toast.error('Failed to load past events');
      setShowImportModal(false);
    } finally {
      setPastEventsLoading(false);
    }
  };

  const handleSelectPastEvent = async (pastEvent: DJEvent) => {
    setSelectedPastEvent(pastEvent);
    setImportStep('pick-songs');
    setPastSongsLoading(true);
    try {
      const requests = await getRequestsByEventId(pastEvent.id);
      const importable = requests.filter(r => r.status === 'queued' || r.status === 'played' || r.status === 'nowPlaying');
      setPastEventSongs(importable);
      const preselected = selectedModeConfig ? partitionForImport(importable, selectedModeConfig).fits : importable;
      setSelectedSongIds(new Set(preselected.map(r => r.id)));
    } catch {
      toast.error('Failed to load songs from event');
      setImportStep('pick-event');
    } finally {
      setPastSongsLoading(false);
    }
  };

  const toggleSongSelection = (songId: string) => {
    setSelectedSongIds(prev => {
      const next = new Set(prev);
      if (next.has(songId)) next.delete(songId);
      else next.add(songId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedSongIds.size === pastEventSongs.length) {
      setSelectedSongIds(new Set());
    } else {
      setSelectedSongIds(new Set(pastEventSongs.map(r => r.id)));
    }
  };

  const handleConfirmImportSelection = () => {
    const songsToImport = pastEventSongs.filter(r => selectedSongIds.has(r.id));
    if (songsToImport.length === 0) {
      toast.error('No songs selected');
      return;
    }
    setStagedImportSongs(songsToImport);
    setStagedSourceEventName(selectedPastEvent?.name || '');
    setShowImportModal(false);
    toast.success(`${songsToImport.length} song${songsToImport.length !== 1 ? 's' : ''} staged for import`);
  };

  const handleClearStagedImport = () => {
    setStagedImportSongs([]);
    setStagedSourceEventName('');
  };

  const handleCloseImportModal = () => {
    setShowImportModal(false);
    setImportStep('pick-event');
    setSelectedPastEvent(null);
    setPastEventSongs([]);
    setSelectedSongIds(new Set());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate event name (reject whitespace-only)
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Event name is required');
      return;
    }

    // Validate date is not in the past
    // Parse as local date (not UTC) to avoid timezone issues
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [yr, mo, dy] = date.split('-').map(Number);
    const eventDate = new Date(yr, mo - 1, dy);
    if (eventDate < today) {
      setError('Event date cannot be in the past');
      return;
    }

    // Note: end time < start time is valid (event crosses midnight)

    if (!user) {
      setError('You must be logged in to create an event');
      return;
    }

    if (!playMode || !selectedModeConfig) {
      setError('Pick the player this event plays through');
      return;
    }

    setLoading(true);

    let existingEvents: DJEvent[];
    try {
      existingEvents = await getEventsByDjId(user.uid);
    } catch (checkError: unknown) {
      console.error('Could not load events for the duplicate-name check', checkError);
      const checkMessage = 'Could not check your event names. Please try again.';
      setError(checkMessage);
      toast.error(checkMessage);
      setLoading(false);
      return;
    }

    const duplicate = findDuplicateEventName(trimmedName, existingEvents);
    if (duplicate) {
      const duplicateMessage = duplicateEventNameMessage(duplicate);
      setError(duplicateMessage);
      toast.error(duplicateMessage);
      setLoading(false);
      return;
    }

    try {
      const contactInfo =(contactName || contactPhone || contactEmail)
        ? { name: contactName || undefined, phone: contactPhone || undefined, email: contactEmail || undefined }
        : undefined;
      // Compute endDate for midnight-crossing events
      const computedEndDate = (() => {
        if (endTime && time && endTime < time) {
          const d = new Date(date + 'T00:00:00');
          d.setDate(d.getDate() + 1);
          return d.toISOString().slice(0, 10);
        }
        return date;
      })();

      const event = await createEvent(user.uid, {
        name: trimmedName,
        date,
        startTime: time,
        startDate: date,
        endTime: endTime || undefined,
        endDate: computedEndDate,
        location: location || undefined,
        address: address || undefined,
        description: description || undefined,
        contactInfo,
        playMode,
        // Apply active settings (from template or DJ defaults)
        settings: {
          maxQueueSize: activeSettings.maxQueueSize,
          queueMode: activeSettings.queueMode,
          requestsOpen: activeSettings.isActive,
          allowTips: activeSettings.allowTips,
          autoApprove: activeSettings.autoApprove,
          filterExplicit: activeSettings.filterExplicit,
          blockExplicit: activeSettings.blockExplicit,
          warnExplicit: activeSettings.warnExplicit,
          cooldownMinutes: activeSettings.cooldownMinutes,
          maxRequestsPerUser: activeSettings.maxRequestsPerUser,
          downvotingEnabled: activeSettings.queueMode !== 'fifo' && activeSettings.downvotingEnabled,
          voteOutEnabled: activeSettings.voteOutEnabled,
          voteOutThresholdPercent: activeSettings.voteOutThresholdPercent,
          upvoteOverrideEnabled: activeSettings.upvoteOverrideEnabled,
          upvoteOverridePercent: activeSettings.upvoteOverridePercent,
          rankSize: activeSettings.rankSize,
          rankingStalenessMinutes: activeSettings.rankingStalenessMinutes,
          rankingMinParticipants: activeSettings.rankingMinParticipants,
          preEventQueueView: activeSettings.preEventQueueView,
          postCloseQueueView: activeSettings.postCloseQueueView,
          duringEventQueueView: activeSettings.duringEventQueueView,
          crossfadeSeconds: activeSettings.crossfadeSeconds,
          allowAttendeeFlagging: activeSettings.allowAttendeeFlagging,
          autoApplyMatchFromOtherEvents: activeSettings.autoMatchFromLibrary ?? false,
          ...playerQueueSettingsFromDefaults(djDefaults),
        },
      });
      if (makeModeDefault) {
        saveDJDefaults(user.uid, { playMode }).catch(error => {
          console.error('[CreateEventPage] Could not save the default player:', error);
          toast.error('Event created, but your default player was not saved');
        });
      }

      // Import staged songs if any
      let matchRunRequestIds: string[] = [];
      if (stagedImportSongs.length > 0) {
        try {
          const importResult = await importSongsFromPastEvent(event.id, stagedImportSongs, user.uid, selectedModeConfig);
          matchRunRequestIds = importResult.unmatchedRequestIds;
          const totalImported = importResult.importedToQueue + importResult.importedToPending;
          if (totalImported > 0) {
            const parts: string[] = [];
            if (importResult.importedToQueue > 0) parts.push(`${importResult.importedToQueue} to Queue`);
            if (importResult.importedToPending > 0) parts.push(`${importResult.importedToPending} to Pending`);
            if (importResult.skippedDuplicates > 0) parts.push(`${importResult.skippedDuplicates} duplicates skipped`);
            toast.success(`Imported: ${parts.join(', ')}`);
          }
        } catch {
          toast.error('Event created but song import failed — you can import from Event Settings');
        }
      }

      if (pollsComingAlong.length > 0) {
        try {
          await addLibraryPollsToEvent(event.id, user.uid, pollsComingAlong);
        } catch (error) {
          console.error('[CreateEventPage] Could not add library polls:', error);
          toast.error("Event created, but your library polls weren't added. Add them from the Messages page.");
        }
      }

      // Import staged library songs if any
      if (stagedLibrarySongs.length > 0) {
        try {
          let libraryAdded = 0;
          for (const match of stagedLibrarySongs) {
            await createRequest(event.id, libraryRequestData(match, user.uid, selectedModeConfig));
            libraryAdded++;
          }
          if (libraryAdded > 0) {
            toast.success(`${libraryAdded} library song${libraryAdded !== 1 ? 's' : ''} added to queue`);
          }
        } catch {
          toast.error('Event created but library songs import failed');
        }
      }

      // Clear draft on successful creation
      clearDraft();
      toast.success('Event created successfully!');
      if (matchRunRequestIds.length > 0) {
        toast(`Match ${matchRunRequestIds.length} song${matchRunRequestIds.length !== 1 ? 's' : ''} for ${selectedModeConfig.playerName} now.`);
      }
      navigate(`/events/${event.id}/manage`, matchRunRequestIds.length > 0 ? { state: { matchRunRequestIds } } : undefined);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create event';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          aria-label="Go back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Create Event</h1>
          <p className="text-gray-600 dark:text-gray-400">Set up a new DJ event</p>
        </div>
      </div>

      {/* Draft recovered notification */}
      {hasLoadedDraft && isDirty && (
        <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-center gap-2 text-blue-700 dark:text-blue-400 text-sm">
            <RefreshCw className="w-5 h-5 flex-shrink-0" />
            <span>Draft recovered from your last session</span>
          </div>
          <button
            type="button"
            onClick={clearDraft}
            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline"
          >
            Clear draft
          </button>
        </div>
      )}

      {error && (
        <div role="alert" className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="card p-6 space-y-6">
        {/* Template Selector */}
        {templates.length > 0 && (
          <div>
            <label htmlFor="template" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Load from Template (optional)
            </label>
            <div className="relative">
              <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                id="template"
                value={selectedTemplate}
                onChange={(e) => handleTemplateChange(e.target.value)}
                className="input pl-10 pr-10 appearance-none cursor-pointer"
              >
                <option value="">-- Select a template --</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </div>
            {/* Template list with delete buttons */}
            <div className="mt-2 space-y-1">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className={`flex items-center justify-between p-2 rounded-lg text-sm ${
                    selectedTemplate === template.id
                      ? 'bg-primary/10 text-primary'
                      : 'bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  <span>{template.name}</span>
                  <button
                    type="button"
                    onClick={(e) => handleDeleteTemplate(template.id, template.name, e)}
                    className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-danger"
                    title="Delete template"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {pollsComingAlong.length > 0 && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Polls from your library: {pollsComingAlong.map(template => `"${template.title}"`).join(', ')}.{' '}
            <Link to="/settings?tab=polls" className="text-primary hover:underline">Manage</Link>
          </p>
        )}
        {pollLibraryError && (
          <p className="text-sm text-amber-600 dark:text-amber-400">
            Couldn't load your poll library, so no library polls will be added.
          </p>
        )}

        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Event Name
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setValue('name', e.target.value)}
            placeholder="Saturday Night Party"
            className="input"
            required
            minLength={3}
            maxLength={100}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                id="date"
                type="date"
                value={date}
                onChange={(e) => setValue('date', e.target.value)}
                className="input pl-10"
                required
              />
            </div>
          </div>
          <div>
            <label htmlFor="time" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Start Time
            </label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                id="time"
                type="time"
                value={time}
                onChange={(e) => setValue('time', e.target.value)}
                className="input pl-10"
                required
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="endTime" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              End Time (optional)
            </label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                id="endTime"
                type="time"
                value={endTime}
                onChange={(e) => setValue('endTime', e.target.value)}
                className="input pl-10"
              />
            </div>
            {endTime && time && endTime < time && (
              <p className="text-xs text-amber-600 dark:text-amber-400 font-medium mt-1">+1 day (past midnight)</p>
            )}
          </div>
          <div>
            <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Location (optional)
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                id="location"
                type="text"
                value={location}
                onChange={(e) => setValue('location', e.target.value)}
                placeholder="Club Downtown"
                className="input pl-10"
              />
            </div>
          </div>
        </div>

        <div>
          <label htmlFor="address" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Address (optional)
          </label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              id="address"
              type="text"
              value={address}
              onChange={(e) => setValue('address', e.target.value)}
              placeholder="123 Main St, City, State"
              className="input pl-10"
            />
          </div>
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Description (optional)
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setValue('description', e.target.value)}
            placeholder="Tell your audience about this event..."
            rows={3}
            className="input resize-none"
          />
        </div>

        {/* Player (ITER-121) */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Player</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            The app you play this event's music in. It locks once the event gets its first request.
          </p>
          <PlayModePicker value={playMode} onChange={setPlayMode} locked={false} />
          <label className="flex items-center gap-2 mt-3 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={makeModeDefault}
              onChange={(e) => setMakeModeDefault(e.target.checked)}
              className="accent-primary"
            />
            {hasSavedDefaultMode ? 'Update my default' : 'Make this my default'}
          </label>
          {!playMode && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">Pick a player to create the event.</p>
          )}
        </div>

        {/* Contact Info (optional) */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Contact Info (optional)</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label htmlFor="contactName" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  id="contactName"
                  type="text"
                  value={contactName}
                  onChange={(e) => setValue('contactName', e.target.value)}
                  placeholder="DJ Name"
                  className="input pl-9 text-sm"
                />
              </div>
            </div>
            <div>
              <label htmlFor="contactPhone" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Phone
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  id="contactPhone"
                  type="tel"
                  value={contactPhone}
                  onChange={(e) => setValue('contactPhone', e.target.value)}
                  placeholder="(555) 123-4567"
                  className="input pl-9 text-sm"
                />
              </div>
            </div>
            <div>
              <label htmlFor="contactEmail" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  id="contactEmail"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setValue('contactEmail', e.target.value)}
                  placeholder="dj@example.com"
                  className="input pl-9 text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Import from Past Event */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Import from Past Event (optional)</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Pre-load songs from another event into the new event's queue
              </p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowLibraryModal(true)}
                className="btn btn-secondary text-sm"
              >
                Add from Library{stagedLibrarySongs.length > 0 ? ` (${stagedLibrarySongs.length})` : ''}
              </button>
              <button
                type="button"
                onClick={handleOpenImportModal}
                className="btn btn-secondary text-sm"
              >
                <Download className="w-4 h-4 mr-1.5" />
                {stagedImportSongs.length > 0 ? 'Change' : 'Select Songs'}
              </button>
            </div>
          </div>
          {stagedImportSongs.length > 0 && (
            <div className="mt-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {stagedImportSongs.length} song{stagedImportSongs.length !== 1 ? 's' : ''} from "{stagedSourceEventName}"
                </span>
                <button
                  type="button"
                  onClick={handleClearStagedImport}
                  className="text-xs text-red-500 hover:text-red-700 hover:underline"
                >
                  Clear
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Matched songs will go to Queue, unmatched to Pending after event creation
              </p>
            </div>
          )}
        </div>

        {/* Event Settings Preview */}
        <div className={`rounded-lg p-4 ${
          settingsSource === 'template'
            ? 'bg-primary/5 border border-primary/20'
            : 'bg-gray-50 dark:bg-gray-800/50'
        }`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {settingsSource === 'template'
                  ? `Settings from template "${templates.find(t => t.id === selectedTemplate)?.name}"`
                  : 'Event settings from your defaults'}
              </span>
            </div>
            {settingsSource === 'template' && (
              <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                Template
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-400">
            <div>Queue Mode: <span className="font-medium text-gray-900 dark:text-white">
              {activeSettings.queueMode === 'standard' ? 'Standard Voting' :
               activeSettings.queueMode === 'ranked' ? 'Ranked Choice' : 'FIFO'}
            </span></div>
            <div>Max Queue: <span className="font-medium text-gray-900 dark:text-white">{activeSettings.maxQueueSize}</span></div>
            <div>Auto-Approve: <span className="font-medium text-gray-900 dark:text-white">{activeSettings.autoApprove ? 'Yes' : 'No'}</span></div>
            <div>Requests Open: <span className="font-medium text-gray-900 dark:text-white">{activeSettings.isActive ? 'Yes' : 'No'}</span></div>
            <div>Filter Explicit: <span className="font-medium text-gray-900 dark:text-white">{activeSettings.blockExplicit ? 'Yes' : 'No'}</span></div>
            <div>Downvoting: <span className="font-medium text-gray-900 dark:text-white">{activeSettings.downvotingEnabled ? 'Yes' : 'No'}</span></div>
            <div>Cooldown: <span className="font-medium text-gray-900 dark:text-white">{activeSettings.cooldownMinutes}m</span></div>
            <div>Max Requests: <span className="font-medium text-gray-900 dark:text-white">{activeSettings.maxRequestsPerUser}/person</span></div>
            {activeSettings.queueMode === 'ranked' && (
              <>
                <div>Rank Size: <span className="font-medium text-gray-900 dark:text-white">{activeSettings.rankSize}</span></div>
                <div>Staleness: <span className="font-medium text-gray-900 dark:text-white">{activeSettings.rankingStalenessMinutes === 0 ? 'Disabled' : `${activeSettings.rankingStalenessMinutes}m`}</span></div>
                <div>Min Participants: <span className="font-medium text-gray-900 dark:text-white">{activeSettings.rankingMinParticipants}</span></div>
              </>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            {settingsSource === 'template'
              ? 'These settings will be applied to the new event. You can modify them after creation.'
              : <span>Change these in <a href="/settings" className="text-primary hover:underline">Settings → Event Defaults</a></span>}
          </p>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading || !playMode}>
            <Save className="w-5 h-5 mr-2" />
            {loading ? 'Creating...' : 'Create Event'}
          </button>
        </div>
      </form>

      {/* Import from Past Event Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl max-w-lg w-full mx-4 p-6 shadow-2xl max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                {importStep === 'pick-songs' && (
                  <button
                    onClick={() => { setImportStep('pick-event'); setSelectedPastEvent(null); setPastEventSongs([]); setSelectedSongIds(new Set()); }}
                    className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                )}
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <Download className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {importStep === 'pick-event' ? 'Import from Past Event' : `Import from: ${selectedPastEvent?.name}`}
                </h3>
              </div>
              <button
                onClick={handleCloseImportModal}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Step 1: Pick Event */}
            {importStep === 'pick-event' && (
              <>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Select an event to import songs from.
                </p>
                <div className="flex-1 overflow-y-auto space-y-2">
                  {pastEventsLoading ? (
                    <div className="text-center py-8">
                      <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-2" />
                      <p className="text-gray-500">Loading events...</p>
                    </div>
                  ) : pastEvents.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No other events found.</p>
                  ) : (
                    pastEvents.map(pe => (
                      <button
                        key={pe.id}
                        type="button"
                        onClick={() => handleSelectPastEvent(pe)}
                        className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-left transition-colors"
                      >
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{pe.name}</p>
                          <p className="text-sm text-gray-500">{pe.date} &middot; {pe.status}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </button>
                    ))
                  )}
                </div>
              </>
            )}

            {/* Step 2: Pick Songs */}
            {importStep === 'pick-songs' && (
              <>
                {pastSongsLoading ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-2" />
                    <p className="text-gray-500">Loading songs...</p>
                  </div>
                ) : pastEventSongs.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No queued or played songs in this event.</p>
                ) : (
                  <>
                    {/* Select all / count */}
                    <div className="flex items-center justify-between mb-3">
                      <button type="button" onClick={toggleSelectAll} className="text-sm text-primary hover:underline">
                        {selectedSongIds.size === pastEventSongs.length ? 'Deselect All' : 'Select All'}
                      </button>
                      <span className="text-sm text-gray-500">
                        {selectedSongIds.size} of {pastEventSongs.length} selected
                      </span>
                    </div>
                    {/* Song list */}
                    <div className="flex-1 overflow-y-auto space-y-1 mb-4">
                      {pastEventSongs.map(song => (
                        <button
                          key={song.id}
                          type="button"
                          onClick={() => toggleSongSelection(song.id)}
                          className={`w-full flex items-center gap-3 p-2 rounded-lg border transition-colors text-left ${
                            selectedSongIds.has(song.id)
                              ? 'bg-primary/5 border-primary/20'
                              : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
                          }`}
                        >
                          <div className={`flex-shrink-0 ${selectedSongIds.has(song.id) ? 'text-primary' : 'text-gray-400'}`}>
                            {selectedSongIds.has(song.id) ? <Check className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                          </div>
                          {song.song.albumArtUrl && (
                            <img src={song.song.albumArtUrl} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 dark:text-white truncate">{song.song.title}</p>
                            <p className="text-sm text-gray-500 truncate">{song.song.artist}</p>
                          </div>
                          {!selectedModeConfig ? null : partitionForImport([song], selectedModeConfig).fits.length > 0 ? (
                            <span title="Matched — will go to Queue">
                              <Music className="w-4 h-4 text-green-500 flex-shrink-0" />
                            </span>
                          ) : (
                            <span className="text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-1.5 py-0.5 rounded-full flex-shrink-0" title="Will go to Pending for a match">
                              No {selectedModeConfig.playerName} match
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                    {/* Smart routing info */}
                    <p className="text-xs text-gray-500 dark:text-gray-400 py-2">
                      <Music className="w-3.5 h-3.5 text-green-500 inline mr-1" />Matched songs go to Queue &middot;
                      <Clock className="w-3.5 h-3.5 text-amber-500 inline ml-2 mr-1" />Unmatched go to Pending
                    </p>
                    {/* Confirm selection button */}
                    <button
                      type="button"
                      onClick={handleConfirmImportSelection}
                      className="btn btn-primary w-full"
                      disabled={selectedSongIds.size === 0}
                    >
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Stage {selectedSongIds.size} for Import
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Library Queue Modal — pre-creation mode (eventId = null) */}
      {showLibraryModal && user && (
        <LibraryQueueModal
          djUid={user.uid}
          eventId={null}
          existingRequests={[]}
          playMode={selectedModeConfig ?? playModeConfig(LEGACY_PLAY_MODE)}
          onSongsAdded={(matches) => {
            setStagedLibrarySongs(prev => [...prev, ...matches]);
            setShowLibraryModal(false);
          }}
          onClose={() => setShowLibraryModal(false)}
        />
      )}
    </div>
  );
}
